import { ThunkAction } from 'redux-thunk'

import { PersonFormData } from 'admin/components/pages/PersonForm'
import { SimpleInvoiceRecipientType } from 'admin/components/pages/Purchasers/PurchaserForm'
import config from 'admin/config/main'
import { reduxDependenciesType } from 'admin/redux/create'
import { State } from 'admin/redux/ducks'
import { PaymentPurchaserStatus, PaymentPurchaserType } from 'admin/types'
import createApiReducer, {
  requestDataFor,
  receiveDataFor,
  handleErrorFor,
} from 'redux/utils/createApiDuck'
import { APIState, PersonType, ErrorType } from 'types'

const moduleName = `${config  }/purchaser`

export default createApiReducer(moduleName)

export function fetchPerson(
  orgAccountId: string,
  kimId: number,
  status?: PaymentPurchaserStatus | PaymentPurchaserStatus[],
): ThunkAction<Promise<any>, State, reduxDependenciesType> {
  return (dispatch, getState, { api }) => {
    dispatch(requestDataFor(moduleName)())
    return api
      .getPersonInfo(orgAccountId, kimId, status)
      .then(personInfo => {
        dispatch(receiveDataFor(moduleName)(personInfo))
        return personInfo
      })
      .catch(error => {
        dispatch(handleErrorFor(moduleName)(error))
      })
  }
}

export function getPaymentPurchaser(
  urlSafeCardToken: string,
): ThunkAction<Promise<any>, State, reduxDependenciesType> {
  return (dispatch, getState, { api }) => {
    dispatch(requestDataFor(moduleName)())
    return api
      .getPaymentPurchaser(urlSafeCardToken)
      .then(personInfo => {
        dispatch(receiveDataFor(moduleName)(personInfo))
        return personInfo
      })
      .catch(error => {
        dispatch(handleErrorFor(moduleName)(error))
      })
  }
}

export function updatePerson(
  orgAccountId: string,
  kimId: number,
  requestData: PersonFormData,
): ThunkAction<Promise<any>, State, reduxDependenciesType> {
  return (dispatch, getState, { api }) => {
    dispatch(requestDataFor(moduleName)())
    return api
      .updatePersonInfo(orgAccountId, kimId, requestData)
      .then(response => {
        dispatch(receiveDataFor(moduleName)(response))
      })
      .catch(error => {
        dispatch(handleErrorFor(moduleName)(error))
      })
  }
}

export function createPurchaser(
  orgAccountId: string,
  person: PersonType,
  newPurchasers: SimpleInvoiceRecipientType[],
): ThunkAction<Promise<any>, State, reduxDependenciesType> {
  return async (dispatch, getState, { api }) => {
    dispatch(requestDataFor(moduleName)())
    try {
      let purchaserId
      for (const newPurchaser of newPurchasers) {
        if (person.kimId) {
          const { personalId, ...rest } = person
          purchaserId = await api.createPaymentPurchaser(
            rest,
            newPurchaser.invoiceRecipientId,
            newPurchaser.purchaseLimit as number,
          )
        } else {
          purchaserId = await api.createPaymentPurchaser(
            person,
            newPurchaser.invoiceRecipientId,
            newPurchaser.purchaseLimit as number,
          )
        }
      }
      const purchaser = await api.getPaymentPurchaserForId(
        purchaserId as string,
      )
      dispatch(
        fetchPerson(
          orgAccountId,
          (purchaser as PaymentPurchaserType).purchaserKimId,
        ),
      )
      return Promise.resolve((purchaser as PaymentPurchaserType).purchaserKimId)
    } catch (error) {
      dispatch(handleErrorFor(moduleName)(error))
      return Promise.reject(error)
    }
  }
}

export function inactivatePurchaser(
  purchaserId: string | string[],
): ThunkAction<Promise<any>, State, reduxDependenciesType> {
  return async (dispatch, getState, { api }: reduxDependenciesType) => {
    dispatch(requestDataFor(moduleName)())
    const purchaserIdList: string[] = Array.isArray(purchaserId)
      ? purchaserId
      : [purchaserId]
    const inactivatePurchaserRequests = []
    for (const id of purchaserIdList) {
      inactivatePurchaserRequests.push(await api.inactivatePurchaser(id))
    }
    return Promise.all(inactivatePurchaserRequests).catch(error =>
      handleErrorFor(moduleName)(error),
    )
  }
}

export function updatePurchaser(
  orgAccountId: string,
  person: PersonType,
  newPurchasers: SimpleInvoiceRecipientType[],
  updatedPurchasers: SimpleInvoiceRecipientType[],
  inactivatedPurchasers: SimpleInvoiceRecipientType[],
): ThunkAction<Promise<any>, State, reduxDependenciesType> {
  return async (dispatch, getState, { api }) => {
    dispatch(requestDataFor(moduleName)())
    try {
      await api.updatePersonInfo(orgAccountId, person.kimId, person)
      for (const purchaser of newPurchasers) {
        if (person.kimId) {
          const { personalId, ...rest } = person
          await api.createPaymentPurchaser(
            rest,
            purchaser.invoiceRecipientId,
            purchaser.purchaseLimit as number,
          )
        } else {
          await api.createPaymentPurchaser(
            person,
            purchaser.invoiceRecipientId,
            purchaser.purchaseLimit as number,
          )
        }
      }
      for (const purchaser of updatedPurchasers) {
        await api.updatePurchaser(
          purchaser.purchaserId,
          purchaser.purchaseLimit as number,
        )
      }
      for (const purchaser of inactivatedPurchasers) {
        await api.inactivatePurchaser(purchaser.purchaserId)
      }
      dispatch(fetchPerson(orgAccountId, person.kimId))
    } catch (error) {
      dispatch(handleErrorFor(moduleName)(error))
    }
  }
}

export type PurchaserType = {
  person: PersonType
  paymentPurchasers: PaymentPurchaserType[]
}
export interface PurchaserState extends APIState {
  data: PurchaserType
  isLoading: boolean
  error: ErrorType | null
}
